import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid2 as Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Chip, Alert, Snackbar,
  IconButton, Tooltip, CircularProgress, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  InputAdornment,
} from '@mui/material';
import {
  Add, Refresh, OpenInNew, CheckCircle, Cancel,
  Business, People, ShoppingCart, Storefront, PersonAdd, Visibility, VisibilityOff,
  Launch,
} from '@mui/icons-material';
import DataTable, { Column, DateCell } from '../../components/Common/DataTable';
import numishubService, {
  NumisHubTenant, NumisHubStats, NumisHubTenantStats, CreateTenantInput,
  NumisHubUser, CreateTenantUserInput,
} from '../../services/numishub.service';

const PLAN_COLOR: Record<string, 'default' | 'primary' | 'secondary' | 'success'> = {
  starter: 'default', pro: 'primary', enterprise: 'success',
};
const COUNTRIES: { code: string; name: string }[] = [
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'PT', name: 'Portugal' },
  { code: 'AO', name: 'Angola' },
  { code: 'MZ', name: 'Moçambique' },
  { code: 'BR', name: 'Brasil' },
  { code: 'ST', name: 'São Tomé e Príncipe' },
  { code: 'GW', name: 'Guiné-Bissau' },
  { code: 'MU', name: 'Maurícias' },
];
const EMPTY_FORM: CreateTenantInput = {
  slug: '', name: '', country: 'CV', country_name: 'Cabo Verde', plan: 'starter', domain: '',
};
const EMPTY_USER_FORM: CreateTenantUserInput = {
  name: '', email: '', password: '', role: 'bank_admin',
};

const NumisHubAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants]         = useState<NumisHubTenant[]>([]);
  const [stats, setStats]             = useState<NumisHubStats | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [successMsg, setSuccessMsg]   = useState('');

  const [createOpen, setCreateOpen]   = useState(false);
  const [form, setForm]               = useState<CreateTenantInput>(EMPTY_FORM);
  const [creating, setCreating]       = useState(false);
  const [formError, setFormError]     = useState('');

  const [detailOpen, setDetailOpen]   = useState(false);
  const [selected, setSelected]       = useState<NumisHubTenant | null>(null);
  const [tenantStats, setTenantStats] = useState<NumisHubTenantStats | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab]     = useState(0);

  const [tenantUsers, setTenantUsers]     = useState<NumisHubUser[]>([]);
  const [usersLoading, setUsersLoading]   = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm]           = useState<CreateTenantUserInput>(EMPTY_USER_FORM);
  const [creatingUser, setCreatingUser]   = useState(false);
  const [userFormError, setUserFormError] = useState('');
  const [showPassword, setShowPassword]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { tenants: t, stats: s } = await numishubService.listTenants();
      setTenants(t); setStats(s);
    } catch {
      setError('Não foi possível ligar ao NumisHub API. Verifique se está em execução.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (tenant: NumisHubTenant) => {
    setSelected(tenant); setDetailOpen(true); setTenantStats(null); setDetailLoading(true);
    setDetailTab(0); setTenantUsers([]);
    try {
      const { stats: s } = await numishubService.getTenant(tenant.slug);
      setTenantStats(s);
    } catch {} finally { setDetailLoading(false); }
  };

  const loadTenantUsers = useCallback(async (slug: string) => {
    setUsersLoading(true);
    try {
      const users = await numishubService.listTenantUsers(slug);
      setTenantUsers(users);
    } catch { setTenantUsers([]); }
    finally { setUsersLoading(false); }
  }, []);

  const handleDetailTabChange = (_: React.SyntheticEvent, v: number) => {
    setDetailTab(v);
    if (v === 1 && selected && !tenantUsers.length) loadTenantUsers(selected.slug);
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      setUserFormError('Preencha todos os campos obrigatórios.'); return;
    }
    if (!selected) return;
    setCreatingUser(true); setUserFormError('');
    try {
      await numishubService.createTenantUser(selected.slug, userForm);
      setUserDialogOpen(false);
      setUserForm(EMPTY_USER_FORM);
      setSuccessMsg(`Utilizador '${userForm.email}' criado com sucesso.`);
      loadTenantUsers(selected.slug);
    } catch (err: any) {
      setUserFormError(err.response?.data?.error ?? err.message ?? 'Erro ao criar utilizador');
    } finally { setCreatingUser(false); }
  };

  const handleCreate = async () => {
    if (!form.slug || !form.name || !form.country) {
      setFormError('Preencha todos os campos obrigatórios.'); return;
    }
    setCreating(true); setFormError('');
    try {
      await numishubService.createTenant(form);
      setCreateOpen(false); setForm(EMPTY_FORM);
      setSuccessMsg(`Tenant '${form.slug}' criado e BD provisionada!`);
      load();
    } catch (err: any) {
      setFormError(err.response?.data?.error ?? err.message ?? 'Erro ao criar tenant');
    } finally { setCreating(false); }
  };

  const toggleActive = async (tenant: NumisHubTenant) => {
    try {
      await numishubService.updateTenant(tenant.slug, { active: tenant.active ? 0 : 1 });
      setSuccessMsg(`Tenant '${tenant.slug}' ${tenant.active ? 'desactivado' : 'activado'}.`);
      if (selected?.slug === tenant.slug) setSelected({ ...tenant, active: tenant.active ? 0 : 1 });
      load();
    } catch { setError('Erro ao actualizar tenant'); }
  };

  const handlePlanChange = async (slug: string, plan: string) => {
    try {
      await numishubService.updateTenant(slug, { plan: plan as any });
      setSuccessMsg('Plano actualizado.');
      load();
    } catch { setError('Erro ao actualizar plano'); }
  };

  const setField = (k: keyof CreateTenantInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const val = e.target.value;
      setForm(f => {
        const upd: CreateTenantInput = { ...f, [k]: val };
        if (k === 'name' && !f.slug) {
          upd.slug = val.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        }
        if (k === 'country') {
          upd.country_name = COUNTRIES.find(c => c.code === val)?.name ?? val;
        }
        return upd;
      });
    };

  const columns: Column<NumisHubTenant>[] = [
    {
      id: 'name', label: 'Banco / Entidade', minWidth: 180,
      format: (_, r) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">{r.name}</Typography>
          <Typography variant="caption" color="text.secondary">{r.slug}</Typography>
        </Box>
      ),
    },
    { id: 'country_name', label: 'País', minWidth: 120 },
    {
      id: 'plan', label: 'Plano', minWidth: 100,
      format: (_, r) => <Chip label={r.plan} color={PLAN_COLOR[r.plan]} size="small" />,
    },
    {
      id: 'active', label: 'Estado', minWidth: 100,
      format: (_, r) => (
        <Chip
          label={r.active ? 'Activo' : 'Inactivo'}
          color={r.active ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'db_exists', label: 'BD', minWidth: 60,
      format: (_, r) => r.db_exists
        ? <CheckCircle color="success" fontSize="small" />
        : <Cancel color="disabled" fontSize="small" />,
    },
    {
      id: 'domain', label: 'Domínio', minWidth: 160,
      format: (_, r) => r.domain
        ? <Typography variant="caption">{r.domain}</Typography>
        : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    { id: 'created_at', label: 'Criado em', minWidth: 120, format: (_, r) => <DateCell value={r.created_at} /> },
    {
      id: 'actions' as any, label: '', minWidth: 100,
      format: (_, r) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Página completa">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/numishub/${r.slug}`); }}>
              <Launch fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver resumo">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDetail(r); }}>
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={r.active ? 'Desactivar' : 'Activar'}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleActive(r); }}>
              {r.active ? <Cancel fontSize="small" color="warning" /> : <CheckCircle fontSize="small" color="success" />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">NumisHub</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestão de bancos centrais e subscrições
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Actualizar</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Novo Tenant
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total', value: stats.total, icon: <Business /> },
            { label: 'Activos', value: stats.active, icon: <CheckCircle color="success" /> },
            { label: 'Plano Pro', value: stats.pro, icon: <Storefront color="primary" /> },
            { label: 'Enterprise', value: stats.enterprise, icon: <Storefront color="success" /> },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">{s.value ?? 0}</Typography>
                      <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                    </Box>
                    {s.icon}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tabela */}
      <DataTable
        columns={columns}
        data={tenants}
        loading={loading}
        onView={openDetail}
        emptyMessage="Nenhum tenant registado"
        searchPlaceholder="Pesquisar por nome, slug ou país..."
      />

      {/* ── Dialog: Criar Tenant ────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Tenant NumisHub</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField label="Nome da entidade *" value={form.name}
              onChange={setField('name')} fullWidth />
            <TextField label="Slug (ID único) *" value={form.slug}
              onChange={setField('slug')} fullWidth
              helperText="Letras minúsculas, números e hífens. Usado como ID do tenant e nome da BD." />
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField select label="País *" value={form.country}
                  onChange={setField('country')} fullWidth>
                  {COUNTRIES.map(c => (
                    <MenuItem key={c.code} value={c.code}>{c.name} ({c.code})</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField select label="Plano" value={form.plan ?? 'starter'}
                  onChange={setField('plan')} fullWidth>
                  <MenuItem value="starter">Starter</MenuItem>
                  <MenuItem value="pro">Pro</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <TextField label="Domínio personalizado" value={form.domain ?? ''}
              onChange={setField('domain')} fullWidth
              placeholder="numismatica.bcv.cv"
              helperText="Opcional. Deixar em branco para usar subdomínio numishub.org." />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <Add />}>
            {creating ? 'A criar...' : 'Criar e Provisionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Detalhe Tenant ──────────────────────────────── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">{selected.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{selected.slug}</Typography>
                </Box>
                <Chip
                  label={selected.active ? 'Activo' : 'Inactivo'}
                  color={selected.active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Tabs value={detailTab} onChange={handleDetailTabChange} sx={{ mt: 1 }}>
                <Tab label="Visão geral" />
                <Tab label="Utilizadores" icon={<People fontSize="small" />} iconPosition="start" />
              </Tabs>
            </DialogTitle>
            <DialogContent>
              {/* Tab 0 — Visão geral */}
              {detailTab === 0 && (
                detailLoading ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                  <Grid container spacing={2} sx={{ pt: 1 }}>
                    {/* Info */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Informação</Typography>
                      {[
                        ['País', `${selected.country_name} (${selected.country})`],
                        ['Domínio', selected.domain || '—'],
                        ['BD', `numishub_${selected.slug}`],
                        ['Plano', selected.plan],
                        ['Criado em', new Date(selected.created_at).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })],
                      ].map(([k, v]) => (
                        <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" color="text.secondary">{k}</Typography>
                          <Typography variant="body2" fontWeight="medium">{v}</Typography>
                        </Box>
                      ))}
                    </Grid>

                    {/* Stats BD */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Dados da plataforma</Typography>
                      {tenantStats ? (
                        <Grid container spacing={1}>
                          {[
                            { icon: <Storefront fontSize="small" />, label: 'Peças', value: tenantStats.coins?.total ?? 0 },
                            { icon: <People fontSize="small" />,     label: 'Colecionadores', value: tenantStats.users?.total ?? 0 },
                            { icon: <ShoppingCart fontSize="small" />, label: 'Encomendas', value: tenantStats.orders?.total ?? 0 },
                            { icon: <Business fontSize="small" />,   label: 'Receita', value: `€${Number(tenantStats.orders?.revenue ?? 0).toFixed(2)}` },
                          ].map(s => (
                            <Grid key={s.label} size={{ xs: 6 }}>
                              <Card variant="outlined">
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {s.icon}
                                    <Box>
                                      <Typography variant="h6" fontWeight="bold" lineHeight={1}>{s.value}</Typography>
                                      <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Alert severity="info" sx={{ mt: 1 }}>BD não acessível ou sem dados.</Alert>
                      )}
                    </Grid>

                    {/* Plano */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Plano</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {['starter', 'pro', 'enterprise'].map(p => (
                          <Button
                            key={p}
                            variant={selected.plan === p ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => handlePlanChange(selected.slug, p)}
                          >
                            {p.charAt(0).toUpperCase() + p.slice(1)}
                          </Button>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                )
              )}

              {/* Tab 1 — Utilizadores */}
              {detailTab === 1 && (
                <Box sx={{ pt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PersonAdd />}
                      onClick={() => { setUserForm(EMPTY_USER_FORM); setUserFormError(''); setUserDialogOpen(true); }}
                    >
                      Criar Utilizador
                    </Button>
                  </Box>
                  {usersLoading ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
                  ) : tenantUsers.length === 0 ? (
                    <Alert severity="info">Nenhum utilizador encontrado para este tenant.</Alert>
                  ) : (
                    <Paper variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Papel</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Criado em</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tenantUsers.map(u => (
                            <TableRow key={u.id} hover>
                              <TableCell>{u.name}</TableCell>
                              <TableCell>{u.email}</TableCell>
                              <TableCell>
                                <Chip
                                  label={u.role === 'bank_admin' ? 'Admin' : 'Staff'}
                                  color={u.role === 'bank_admin' ? 'primary' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={u.active ? 'Activo' : 'Inactivo'}
                                  color={u.active ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {new Date(u.created_at).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                color={selected.active ? 'warning' : 'success'}
                onClick={() => { toggleActive(selected); setDetailOpen(false); }}
              >
                {selected.active ? 'Desactivar tenant' : 'Activar tenant'}
              </Button>
              <Button onClick={() => setDetailOpen(false)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Dialog: Criar Utilizador ────────────────────────────── */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box>
            <Typography variant="h6" fontWeight="bold">Criar Utilizador</Typography>
            <Typography variant="caption" color="text.secondary">{selected?.name}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {userFormError && <Alert severity="error">{userFormError}</Alert>}
            <TextField
              label="Nome completo *"
              value={userForm.name}
              onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Email *"
              type="email"
              value={userForm.email}
              onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Password *"
              type={showPassword ? 'text' : 'password'}
              value={userForm.password}
              onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size="small">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Papel"
              value={userForm.role}
              onChange={e => setUserForm(f => ({ ...f, role: e.target.value as any }))}
              fullWidth
            >
              <MenuItem value="bank_admin">Admin do banco (bank_admin)</MenuItem>
              <MenuItem value="bank_staff">Staff do banco (bank_staff)</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={creatingUser}
            startIcon={creatingUser ? <CircularProgress size={16} /> : <PersonAdd />}
          >
            {creatingUser ? 'A criar...' : 'Criar Utilizador'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMsg('')}>{successMsg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default NumisHubAdmin;
